import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
// Materials
import { Grid, Typography } from "@mui/material";
// API's
import { createChangeRequest } from "@/Washouts/api/WashoutReview/mutations";
// Hooks
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";
import { useSingularPosition } from "@/Common/hooks/Positions";
// Custom components
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import RenderWashoutInformation from "@/Washouts/components/RenderWashoutInformation";
import { ChangeRequestContainer } from "@/Washouts/components/ChangeRequestContainer";
import ChangeRequestPositionedPopper from "@/Washouts/components/ChangeRequestPositionedPopper";
// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";
// Helpers
import { normalizePositions } from "@/Common/helpers/Positions";
import { parseIdsFromSelectedTreeNode } from "@/Washouts/helpers/ChangeRequest";
import { createOrUpdateChangeRequestCallback } from "@/Washouts/helpers/ChangeRequest/subscriptionCallbacks";
// Types
import { RootState } from "@/redux/store";
import { IWashoutQueries } from "@/Washouts/types/WashoutReview";
import { IGetChangeRequestsByCommissionCycle } from "@/Washouts/types/PayrollClosing/queries";
import { ExtendedTreeNode, ISupportRequestsSidebarProps } from "./Sidebar";

const EmptyContainer = ({ hasSelection }: { hasSelection: boolean }) => (
	<RoundedBox>
		<EmptyBody
			image={hasSelection ? emptyTable : empty}
			title={hasSelection ? "No washout found for this employee" : "Select an employee to view their washout"}
			className="!shadow-none"
			bodyHeight="510px"
		/>
	</RoundedBox>
);

export interface ISupportRequestsContainerProps {
	currentTree: ISupportRequestsSidebarProps["currentTree"];
	selected: ExtendedTreeNode | null;
	canCreateChangeRequests: boolean;
}

export default function SupportRequestsContainer(props: Readonly<ISupportRequestsContainerProps>) {
	const { selected, currentTree, canCreateChangeRequests } = props;

	const locationId = useSelector((state: RootState) => state.location.location?.locationId ?? "");
	const currentLoggedUserId = useSelector((state: RootState) => state.user.user?._id ?? "");
	const { commissionCycle, payPeriod, position, user } = parseIdsFromSelectedTreeNode(selected, currentLoggedUserId);

	const selectedPosition = normalizePositions(position ?? "");
	const { useWashoutQueries } = useFactoryWashout() as unknown as { useWashoutQueries: (position: string) => IWashoutQueries };
	const { getWashout } = useWashoutQueries(selectedPosition);

	const { data, isLoading } = useQuery({
		queryKey: ["associateWashoutsReview", user ?? currentLoggedUserId, locationId, commissionCycle, payPeriod, position],
		queryFn: () => getWashout(locationId, user ?? currentLoggedUserId, commissionCycle, payPeriod, position),
		enabled: !!locationId && Boolean(selected),
		staleTime: Infinity,
	});

	const positionToUse = useSingularPosition(position);

	if (!selected) {
		return <EmptyContainer hasSelection={Boolean(selected)} />;
	}

	return (
		<Grid container direction="column" className="shadow-white-section" rowSpacing={0}>
			<ChangeRequestSection
				asReviewer={currentTree === "ReviewTree"}
				canCreateChangeRequest={currentTree === "CurrentMyTree" && canCreateChangeRequests}
				filterByPayPeriod={currentTree !== "CurrentMyTree"}
				locationId={locationId}
				position={position}
				commissionCycleId={commissionCycle}
				payPeriodId={payPeriod}
				userId={user ?? currentLoggedUserId}
				readonly={selected?.readonly}
			/>

			<RenderWashoutInformation
				position={positionToUse}
				properties={{
					isLoading,
					editable: false,
					model: data,
					hideEmployeeMeta: false,
					disableBoxShadow: true,
					employeeMetaProps: {
						hiddenReportButton: true,
						containerProps: {
							roundedTop: false,
						},
					},
				}}
			/>
		</Grid>
	);
}

type TChangeRequestSectionProps = {
	position: string;
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	userId: string;
	canCreateChangeRequest: boolean;
	asReviewer: boolean;
	filterByPayPeriod: boolean;
	readonly?: boolean;
};

export const ChangeRequestSection = (props: TChangeRequestSectionProps) => {
	const {
		position,
		locationId,
		commissionCycleId,
		payPeriodId,
		userId,
		canCreateChangeRequest,
		filterByPayPeriod,
		asReviewer,
		readonly,
	} = props;
	const queryClient = useQueryClient();

	const origin = "SUPPORT";
	const [openCreateChangeRequestPopper, setOpenCreateChangeRequestPopper] = useState<boolean>(false);

	const { mutate: createChangeRequestMutate, isPending: isPendingCreateChangeRequest } = useMutation({
		mutationKey: ["createChangeRequest", locationId, commissionCycleId, payPeriodId, origin, userId, position],
		mutationFn: (input: { description?: string; issueSummary: string; origin: string; position: string }) =>
			createChangeRequest({ locationId, payPeriodId, userId, input, commissionCycleId }),
		onSuccess: ({ data }: { data: { createChangeRequest: IGetChangeRequestsByCommissionCycle } }) => {
			createOrUpdateChangeRequestCallback(data.createChangeRequest, queryClient, true);
		},
	});

	const handleSaveRequest = (issueSummary: string, description: string) => {
		const input = {
			issueSummary,
			description,
			origin,
			position,
		};
		createChangeRequestMutate(input);
	};

	return (
		<>
			<Grid container className="flex justify-between pt-5 px-5">
				<Typography className="!font-montserrat !font-medium !text-[24px]/[1.75rem] pl-[10px] text-[color:--text-color]">
					Request Support
				</Typography>
				<ChangeRequestPositionedPopper
					disabled={!canCreateChangeRequest}
					handleSaveRequest={handleSaveRequest}
					open={openCreateChangeRequestPopper}
					onOpen={setOpenCreateChangeRequestPopper}
					changeRequestButtonClassname="!bg-primary"
					label="Request Support"
				/>
			</Grid>
			<Grid item container>
				<Grid item xs={12} className="pt-5 pl-[30px] pb-10 relative !mr-5">
					<ChangeRequestContainer
						payPeriodId={payPeriodId}
						locationId={locationId}
						commissionCycleId={commissionCycleId}
						origin={origin}
						userId={userId}
						position={position}
						skeletonRows={isPendingCreateChangeRequest ? [""] : []}
						tableProps={{
							allowDeletion: true,
							switchValue: "PENDING",
						}}
						hideOpenWashout={asReviewer ? true : undefined}
						disableRowClick={openCreateChangeRequestPopper}
						filterByPayPeriod={filterByPayPeriod}
						disableStatusAction={readonly || !asReviewer ? true : undefined}
						statusActionTooltipText={asReviewer ? "Resolve from Support Center" : undefined}
					/>
				</Grid>
			</Grid>
		</>
	);
};
